import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '~/routes/query-params';

import * as S from './RankingMethodologyDrawer.styles';
import { useFlags } from '~/stores/use-flags/useFlags';

export type RankingMethodologyType =
  | 'companyToTheme'
  | 'themeToCompany'
  | 'competitorsToTheme'
  | 'companyToGeographicExposure'
  | 'companyToBrandAndProduct';

export const RankingMethodologyDrawer = () => {
  const { t } = useTranslation(['widget']);
  const { params, deleteQueryParam, setQueryParams } = useQueryParams();
  const rankingMethodologyType = params[queryKeys.mode] || 'companyToTheme';
  const isBrandsAndProductsEnabled = useFlags(state =>
    state.getFeatureFlagState('kg_brands_products')
  );
  const tabs = [
    {
      tabLabel: t(
        'assetOverview.knowledgeGraph.rankingMethodology.tabs.themeExposure'
      ),
      key: 'companyToTheme',
    },
    ...(isBrandsAndProductsEnabled ? [{
      tabLabel: t(
        'assetOverview.knowledgeGraph.rankingMethodology.tabs.brandsAndProducts'
      ),
      key: 'companyToBrandAndProduct',
    }] : []),
    {
      tabLabel: t(
        'assetOverview.knowledgeGraph.rankingMethodology.tabs.competitors'
      ),
      key: 'competitorsToTheme',
    },
    {
      tabLabel: t(
        'assetOverview.knowledgeGraph.rankingMethodology.tabs.geographicExposure'
      ),
      key: 'companyToGeographicExposure',
    },
  ];
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    const tabIndex = tabs.findIndex(t => t.key === rankingMethodologyType);
    setActiveTabIndex(tabIndex);
  }, [rankingMethodologyType]);

  const isThemeDrawer = rankingMethodologyType === 'themeToCompany';

  const base = 'widget:assetOverview.knowledgeGraph.rankingMethodology';
  const title = t(`${base}.${rankingMethodologyType}.title`);
  const accordionItems = Object.values(
    t(`${base}.${rankingMethodologyType}.accordionItems`, {
      returnObjects: true,
    }) as Record<string, { title: string; content: string }>
  );

  const isDrawerOpen =
    params[queryKeys.activeDrawer] === queryValues.rankingMethodologyDrawer;

  const setActiveIndex = (index: number) => {
    setActiveTabIndex(index);
    setQueryParams({ [queryKeys.mode]: tabs[index].key });
  };

  return (
    <S.StyledDrawer
      data-testid="ranking-methodology-drawer"
      isOpen={isDrawerOpen}
      handleCloseClick={() =>
        deleteQueryParam([queryKeys.activeDrawer, queryKeys.mode])
      }
    >
      <S.ContentWrapper>
        <S.DrawerTitle>
          {t('widget:assetOverview.knowledgeGraph.rankingMethodology.name')}
        </S.DrawerTitle>
        {!isThemeDrawer && (
          <S.StyledButtonGroup
            labels={tabs.map(t => t.tabLabel)}
            activeIndex={activeTabIndex}
            onClick={setActiveIndex}
          />
        )}
        {title && <S.Title>{title}</S.Title>}
        <S.StyledAccordion items={accordionItems} />
      </S.ContentWrapper>
    </S.StyledDrawer>
  );
};
